import "./area01Products.css";
import { useEffect, useState } from "react";
import {
  Board,
  BoardBody,
  BoardHead,
  BoardTd,
  BoardTh,
  BoardTr,
  Edit,
  IconButton,
  Modal,
  OperationArea,
  Panel,
} from "../../../components";
import { TrashOutline } from "../../../assets";
import { useApiFetch, useLanguage } from "../../../hooks";
import {
  AdminArea01CreateResultProps,
  AdminArea01ProductProps,
  API_URL,
} from "../../../services";
import { formatDateToLocal } from "../../../utils";

// Una línea por producto: "EAN" o "EAN;nombre" (también tabulador, para poder
// pegar dos columnas de Excel). La validación del EAN la hace el back.
const parseLines = (value: string) =>
  value
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [ean, ...name] = line.split(/[;\t]/);
      return { ean: ean.trim(), productName: name.join(" ").trim() };
    });

const Area01ProductsScreen: React.FC = () => {
  const { t } = useLanguage();
  const s = t.adminArea01Screen;

  const [fetchProducts, products, loading] = useApiFetch<
    AdminArea01ProductProps[]
  >([]);
  const [fetchCreate, , creating, createError] =
    useApiFetch<AdminArea01CreateResultProps>();
  const [fetchDelete, , deleting, deleteError] = useApiFetch<null>();

  const [creatingOpen, setCreatingOpen] = useState<boolean>(false);
  const [bulkText, setBulkText] = useState<string>("");
  const [lastResult, setLastResult] =
    useState<AdminArea01CreateResultProps | null>(null);
  const [deletingProduct, setDeletingProduct] =
    useState<AdminArea01ProductProps | null>(null);

  const load = async () => {
    await fetchProducts(API_URL.ADMIN_AREA01_PRODUCTS(), { method: "GET" });
  };

  useEffect(() => {
    load();
  }, []);

  const items = parseLines(bulkText);

  const handleCreate = async () => {
    const result = await fetchCreate(API_URL.ADMIN_AREA01_PRODUCTS(), {
      method: "POST",
      body: JSON.stringify({ items }),
    });

    if (!result?.data) {
      return;
    }

    setLastResult(result.data);
    setCreatingOpen(false);
    setBulkText("");
    await load();
  };

  const handleDelete = async () => {
    if (!deletingProduct) {
      return;
    }

    const result = await fetchDelete(
      API_URL.ADMIN_AREA01_PRODUCTS(`/${deletingProduct.id}`),
      { method: "DELETE" }
    );

    if (result?.code && result.code >= 400) {
      return;
    }

    setDeletingProduct(null);
    await load();
  };

  return (
    <>
      <Panel>
        <OperationArea
          title={s.title}
          toolbarButton={{
            show: true,
            text: s.create.button,
            onClick: () => setCreatingOpen(true),
          }}
        >
          <p className="area01-description">{s.description}</p>
          {lastResult && (
            <p className="area01-result">
              {lastResult.created.length} {s.create.created} ·{" "}
              {lastResult.skipped.length} {s.create.skipped} ·{" "}
              {lastResult.invalid.length} {s.create.invalid}
              {lastResult.invalid.length > 0 &&
                `: ${lastResult.invalid.join(", ")}`}
            </p>
          )}

          <Board id="table-admin-area01-products">
            <BoardHead>
              <BoardTr>
                <BoardTh text={s.table.thead.ean} />
                <BoardTh text={s.table.thead.productName} />
                <BoardTh text={s.table.thead.createdAt} />
                <BoardTh text={s.table.thead.actions} />
              </BoardTr>
            </BoardHead>

            <BoardBody isLoading={loading}>
              {products.length === 0 && !loading ? (
                <BoardTr>
                  <BoardTd>
                    <p>{s.table.empty}</p>
                  </BoardTd>
                </BoardTr>
              ) : (
                products.map((product) => (
                  <BoardTr key={`area01-product-${product.id}`}>
                    <BoardTd>
                      <p className="board-bold">{product.ean}</p>
                    </BoardTd>
                    <BoardTd hasClassNameW100>
                      <p>{product.productName}</p>
                    </BoardTd>
                    <BoardTd>
                      <p>{formatDateToLocal(product.createdAt)}</p>
                    </BoardTd>
                    <BoardTd hasClassNameDirection={false}>
                      <IconButton
                        icon={TrashOutline}
                        alt={s.delete.deleteButton}
                        variant="danger"
                        onClick={() => setDeletingProduct(product)}
                      />
                    </BoardTd>
                  </BoardTr>
                ))
              )}
            </BoardBody>
          </Board>
        </OperationArea>

        {/* AÑADIR EANs (en bloque) */}
        <Modal
          open={creatingOpen}
          setOpen={setCreatingOpen}
          title={s.create.title}
          actions={[
            {
              text: s.create.confirm,
              onClick: handleCreate,
              disabled: creating || items.length === 0,
            },
          ]}
        >
          <Edit>
            <p className="area01-help">{s.create.help}</p>
            <div className="area01-textarea">
              <Edit.TextArea
                id="area01-bulk"
                value={bulkText}
                placeholder={s.create.placeholder}
                onChange={(e) => setBulkText(e.target.value)}
              />
            </div>
            {createError && <p className="area01-form-error">{createError}</p>}
          </Edit>
        </Modal>

        {/* ELIMINAR */}
        <Modal
          open={deletingProduct !== null}
          setOpen={(open) => !open && setDeletingProduct(null)}
          title={s.delete.title}
          actions={[
            {
              text: s.delete.confirm,
              onClick: handleDelete,
              disabled: deleting,
            },
          ]}
        >
          <p>
            {s.delete.description} {deletingProduct?.ean}?
          </p>
          {deleteError && <p className="area01-form-error">{deleteError}</p>}
        </Modal>
      </Panel>
    </>
  );
};

export default Area01ProductsScreen;
